import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { nextAlterationStatus, type Alteration } from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import { useMoveAlteration } from '../../lib/alterations/use-alterations.js';
import { Button, useToast } from '@tailoring-crm/ui';
import { HandOverAlterationDialog } from './hand-over-alteration-dialog.js';

/**
 * The one forward move an alteration has, as one button — on the list's rows and on its own page.
 *
 * Every step is a single tap except one: handing it back while money is owed, which opens the
 * hand-over dialog so the balance is asked for at the moment it is most likely to be paid. A free
 * alteration, or one already settled, still hands over in one tap.
 */
export function AlterationNextStep({
  alteration,
  size = 'sm',
}: {
  alteration: Alteration;
  size?: 'sm' | 'default';
}): ReactElement | null {
  const { t } = useTranslation();
  const { toast } = useToast();
  const move = useMoveAlteration();
  const [handingOver, setHandingOver] = useState<Alteration | null>(null);

  const next = nextAlterationStatus(alteration.status);
  if (!next) return null;

  const advance = async (): Promise<void> => {
    if (next === 'delivered' && alteration.outstandingPaise > 0) {
      setHandingOver(alteration);
      return;
    }
    try {
      await move.mutateAsync({ alterationId: alteration.id, toStatus: next });
      toast({ title: t(`alterations.movedTo.${next}`), variant: 'success' });
    } catch (caught) {
      toast({
        title: isApiRequestError(caught) ? caught.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Button size={size} disabled={move.isPending} onClick={() => void advance()}>
        {t(`alterations.advanceTo.${next}`)}
      </Button>
      <HandOverAlterationDialog
        alteration={handingOver}
        onOpenChange={(open) => !open && setHandingOver(null)}
      />
    </>
  );
}

export default AlterationNextStep;
